import { createFileRoute } from "@tanstack/react-router";
import type { ClientHomePlanBaseData } from "api-types/homePlans/homePlanResponses";
import type { ClientStudioDocHydrated } from "api-types/studioDocs/studioDocResponses";
import type { FeatureSet } from "common/constants/features";
import LoadingPage from "lib/components/layout/LoadingPage";
import { ShowroomGenerationRuntimeProvider } from "lib/components/showroom2/generation/showroomGenerationRuntime";
import ShowroomPage from "lib/components/showroom2/layout/ShowroomPage";
import ShowroomConfigure from "lib/components/showroom2/pages/ShowroomConfigure";
import { ShowroomProvider } from "lib/components/showroom2/state/useShowroomState";
import { getStudioDocURL } from "lib/endpointHelpers";
import { useFetchImmutable } from "lib/fetcher";
import { useAppRouterParams } from "lib/routeHelpers";
import { useUserContext } from "lib/state/UserContextProvider";
import { createFakePlanData } from "lib/utils/showroom/showroomData";
import { useMemo } from "react";
import withAuthentication from "../../components/withAuthentication";

/**
 * Showroom preview of an in-progress Studio doc. Fetches the doc by
 * `studioDocId` and injects it as fake plan data so the normal showroom
 * viewer can render a design that isn't yet a published plan.
 */
function ShowroomStudio() {
  const { isReady, studioDocId, builderId } = useAppRouterParams();
  const { enabledFeatures, featuresLoaded } = useUserContext();

  // featuresLoaded (not enabledFeatures) gates the page so an empty-but-loaded
  // feature set doesn't get treated as "not loaded yet."
  if (!isReady || !studioDocId || !featuresLoaded || !enabledFeatures) {
    return <LoadingPage />;
  }

  return <ReadyShowroomStudio builderId={builderId} studioDocId={studioDocId} enabledFeatures={enabledFeatures} />;
}

function ReadyShowroomStudio({
  builderId,
  studioDocId,
  enabledFeatures,
}: {
  builderId: string | undefined;
  studioDocId: string;
  enabledFeatures: FeatureSet;
}) {
  const url = getStudioDocURL(studioDocId, builderId, { includeStudioDocBuilders: true });
  // builderId is included in the dep array when present so a builder switch triggers a refetch,
  // but we strip falsy values because createFetchKey short-circuits to null on any falsy entry.
  const { data: studioDoc, error: studioDocError } = useFetchImmutable<ClientStudioDocHydrated>(
    url,
    [studioDocId, builderId].filter(Boolean),
  );

  const planData: ClientHomePlanBaseData | null = useMemo(
    () => (studioDoc ? createFakePlanData(studioDoc, enabledFeatures) : null),
    [studioDoc, enabledFeatures],
  );

  const showroomKey = [builderId, studioDocId].filter(Boolean).join("-") || undefined;

  if (studioDocError) {
    return <ShowroomPage pageTitle="Studio doc unavailable">Studio doc could not be loaded.</ShowroomPage>;
  }

  if (!planData) {
    return <LoadingPage />;
  }

  return (
    <ShowroomProvider key={showroomKey} version="sales">
      <ShowroomGenerationRuntimeProvider planDataOverride={planData}>
        <ShowroomConfigure />
      </ShowroomGenerationRuntimeProvider>
    </ShowroomProvider>
  );
}

export const Route = createFileRoute("/showroom/studio")({
  component: withAuthentication(ShowroomStudio, {
    features: ["showroomUnification"],
    products: ["showroom"],
  }),
});
